import { memo, useState, useMemo, useCallback } from 'react';
import IndexDataLoader from './IndexDataLoader';
import { useSyncRouterValuesToRedux } from '../hooks/useSyncRouterValuesToRedux';
import { useSyncRouterValuesToCache } from '../hooks/useSyncRouterValuesToCache';
import { useSelectedObjectTypeId } from '../../../objectTypeIdContext/hooks/useSelectedObjectTypeId';
import FullPageError from '../../../errorBoundary/FullPageError';
import PageLoadingSpinner from './PageLoadingSpinner';
import IndexPageAlerts from '../../../alerts/IndexPageAlerts';
import IndexViewRedirect from './IndexViewRedirect';
import useMarkOnMount from 'crm-fe-perf/timing/hooks/useMarkOnMount';
import { IndexRouteMounted } from 'crm-fe-perf/timing/marks/crm-index-ui/performanceMarks';
import { predictAdditionalMetricsDimensions } from '../../../lib/metrics/predictAdditionalMetricsDimensions';
import ErrorBoundary from '../../../errorBoundary/ErrorBoundary';
import { VizCoreProvider, usePinnedViews } from 'crm-index-visualization-core';
import { useParams } from 'react-router-dom';
import { VizCoreBridge } from '../../vizCoreSync/VizCoreBridge';
import { clientStorage } from '../../../app/utils/vizCoreClientStorage';
import { VizCoreTableConfigBridge } from '../../vizCoreSync/VizCoreTableConfigBridge';
import { REGISTRY } from './vizRegistry';

/*
 * Extracting this as a memoized component prevents the entire component
 * tree from rerendering when the router values change. This way we only ever listen to the
 * values that come from redux, not those that come from IndexRoute's props. As we transition
 * more to graphql/component state-based solutions we may be able to stop syncing values entirely.
 * The main blocker is data fetching. Once we no longer need objectTypeId for data fetching,
 * we can probably dispense with the whole sync flow entirely.
 */
// @ts-expect-error ts-migrate(2339) FIXME: Property 'objectTypeId' does not exist on type '{}... Remove this comment to see the full error message
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const IndexRouteChildren = /*#__PURE__*/memo(({
  objectTypeId
}) => /*#__PURE__*/_jsxs(ErrorBoundary, {
  ErrorComponent: FullPageError,
  boundaryName: `${objectTypeId}_FullPageError`,
  children: [/*#__PURE__*/_jsx(IndexPageAlerts, {}), /*#__PURE__*/_jsx(IndexDataLoader, {
    children: /*#__PURE__*/_jsx(IndexViewRedirect, {})
  })]
}));
IndexRouteChildren.displayName = 'IndexRouteChildren';

/*
 * This component is the entry point for the index route (objects/:objectTypeId/views/:viewId/:pageType).
 *
 * This is what the waterfall of components and their responsibilities looks like for this route:
 *
 * IndexRoute: Syncs router values to redux **and cache**. When object type is synced, renders IndexDataLoader.
 * IndexDataLoader: Fetches data. During fetch renders loading. On error renders error state. On success, renders IndexPageContainer.
 * IndexViewRedirect: Checks that view/pagetype are specified in the url and are valid. If not, redirects to put valid values in the url. If so, renders IndexPageContainer.
 * IndexPageContainer: Handles "special" query params such as filters/columns, and decides which version of the page to render (redux vs graphql).
 * IndexPageWithReduxSearch: Hits elasticsearch using the redux infrastructure. Always renders IndexPage.
 * IndexPageWithCrmSearch: Hits elasticsearch wtih graphql. Always renders IndexPage.
 * IndexPage: This is the start of app code and renders the page layout.
 *
 * A little bit more about the sync logic:
 *
 * We typically go through two rounds of syncing: One to sync objectTypeId, then another that syncs any values that were missing from the url.
 * objectTypeId is always required, and is a blocker for data fetching. Once we know objectTypeId, we know enough to fetch 95% of the page's data.
 * Once our data is fetched, we use that data to determine what to do about missing values for viewId, pageType, or search query (our secondary params).
 * IndexViewRedirect handles this logic. This process repeats for every change in the url over the app's runtime.
 */

const IndexRoute = () => {
  var _draftContexts$draftK;
  useMarkOnMount(IndexRouteMounted, predictAdditionalMetricsDimensions());
  useSyncRouterValuesToCache();
  const {
    viewId,
    pageType
  } = useParams();
  const [draftContexts, setDraftContexts] = useState({});
  const objectTypeId = useSelectedObjectTypeId();
  const hasCompletedInitialReduxSync = useSyncRouterValuesToRedux();
  const currentViewId = viewId !== null && viewId !== void 0 ? viewId : 'all';
  const draftKey = `${currentViewId}:${objectTypeId}`;
  const currentEditHash = (_draftContexts$draftK = draftContexts[draftKey]) !== null && _draftContexts$draftK !== void 0 ? _draftContexts$draftK : null;
  const handleDraftChange = useCallback(draftContext => {
    const key = `${draftContext.viewId}:${draftContext.objectTypeId}`;
    setDraftContexts(prev => Object.assign({}, prev, {
      [key]: draftContext.editHash
    }));
  }, []);
  const currentDraftContext = useMemo(() => ({
    viewId: currentViewId,
    objectTypeId,
    editHash: currentEditHash
  }), [currentViewId, objectTypeId, currentEditHash]);
  if (!hasCompletedInitialReduxSync) {
    return /*#__PURE__*/_jsx(PageLoadingSpinner, {});
  }
  return /*#__PURE__*/_jsxs(VizCoreProvider, {
    pageType: pageType,
    draftContext: currentDraftContext,
    onDraftChange: handleDraftChange,
    clientStorage: clientStorage,
    vizRegistry: REGISTRY,
    children: [/*#__PURE__*/_jsx(PreFetchViews, {}), /*#__PURE__*/_jsx(VizCoreBridge, {}), /*#__PURE__*/_jsx(VizCoreTableConfigBridge, {}), /*#__PURE__*/_jsx(IndexRouteChildren, {
      objectTypeId: objectTypeId
    })]
  });
};
function PreFetchViews() {
  usePinnedViews();
  return null;
}
export default IndexRoute;